// /app/frontend/src/pages/settings/ClientBrandingPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Palette, Upload, Save, Loader2, ArrowLeft, 
  Image, Type, Eye, RefreshCw, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined color themes
const COLOR_THEMES = [
  { name: 'Teal (Default)', primary: '#0d9488', secondary: '#115e59', accent: '#14b8a6' },
  { name: 'Blue', primary: '#2563eb', secondary: '#1e40af', accent: '#3b82f6' },
  { name: 'Purple', primary: '#7c3aed', secondary: '#5b21b6', accent: '#8b5cf6' },
  { name: 'Emerald', primary: '#059669', secondary: '#047857', accent: '#10b981' },
  { name: 'Rose', primary: '#e11d48', secondary: '#be123c', accent: '#f43f5e' },
  { name: 'Amber', primary: '#d97706', secondary: '#b45309', accent: '#f59e0b' },
  { name: 'Slate', primary: '#475569', secondary: '#334155', accent: '#64748b' },
];

// Available fonts
const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Default)' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro' },
];

const DEFAULT_BRANDING = {
  logo_url: null,
  primary_color: '#0d9488',
  secondary_color: '#115e59',
  accent_color: '#14b8a6',
  header_bg_color: '#0f172a',
  sidebar_bg_color: '#1e293b',
  font_family: 'Inter',
  welcome_message: '',
};

export default function ClientBrandingPage() {
  const navigate = useNavigate();
  const { user, token, isDPO } = useAuth();
  const fileInputRef = useRef(null);
  
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [originalBranding, setOriginalBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (isDPO) {
      fetchClients();
    } else if (user?.client_id) {
      setSelectedClientId(user.client_id);
      fetchBranding(user.client_id);
    } else {
      setLoading(false);
    }
  }, [isDPO, user]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
        if (data.length > 0) {
          setSelectedClientId(data[0].id);
          fetchBranding(data[0].id);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setLoading(false);
    }
  };

  const fetchBranding = async (clientId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/clients/${clientId}/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const brandingData = data.branding || DEFAULT_BRANDING;
        setBranding(brandingData);
        setOriginalBranding(brandingData);
        setLogoPreview(brandingData.logo_url);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    fetchBranding(clientId);
  };

  const handleChange = (key, value) => {
    const newBranding = { ...branding, [key]: value };
    setBranding(newBranding);
    setHasChanges(JSON.stringify(newBranding) !== JSON.stringify(originalBranding));
  };

  const handleThemeSelect = (theme) => {
    const newBranding = {
      ...branding,
      primary_color: theme.primary,
      secondary_color: theme.secondary,
      accent_color: theme.accent,
    };
    setBranding(newBranding);
    setHasChanges(JSON.stringify(newBranding) !== JSON.stringify(originalBranding));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, or SVG');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    setUploadingLogo(true);

    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result;
      setLogoPreview(base64);
      handleChange('logo_url', base64);
      setUploadingLogo(false);
      toast.success('Logo uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    handleChange('logo_url', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!selectedClientId) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/clients/${selectedClientId}/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(branding)
      });

      if (response.ok) {
        toast.success('Branding settings saved successfully');
        setOriginalBranding(branding);
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save branding');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBranding(DEFAULT_BRANDING);
    setLogoPreview(null);
    setHasChanges(true);
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Palette className="h-6 w-6" />
                Portal Branding
              </h1>
              <p className="text-muted-foreground">
                Customize your client portal appearance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="secondary" className="animate-pulse">
                Unsaved changes
              </Badge>
            )}
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Client Selector (DPO only) */}
        {isDPO && clients.length > 0 && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="whitespace-nowrap">Select Client:</Label>
                <Select value={selectedClientId || ""} onValueChange={handleClientChange}>
                  <SelectTrigger className="w-[300px]" data-testid="client-selector">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClient?.subdomain && (
                  <Badge variant="outline">
                    Portal: {selectedClient.subdomain}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Logo
                </CardTitle>
                <CardDescription>
                  Upload your organization's logo for the portal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-48 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden"
                    data-testid="logo-preview"
                  >
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">No logo uploaded</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Logo
                    </Button>
                    {logoPreview && (
                      <Button variant="ghost" size="sm" onClick={handleRemoveLogo}>
                        <X className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended: 200x60px, PNG or SVG, max 2MB
                </p>
              </CardContent>
            </Card>

            {/* Color Theme */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Color Theme
                </CardTitle>
                <CardDescription>
                  Choose a preset theme or customize individual colors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Preset Themes */}
                <div>
                  <Label className="mb-3 block">Quick Themes</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_THEMES.map((theme) => (
                      <button
                        key={theme.name}
                        onClick={() => handleThemeSelect(theme)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                          branding.primary_color === theme.primary 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'hover:border-muted-foreground/50'
                        }`}
                        data-testid={`theme-${theme.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: theme.primary }}
                        />
                        <span className="text-sm">{theme.name}</span>
                        {branding.primary_color === theme.primary && (
                          <Check className="h-3 w-3 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Custom Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.accent_color}
                        onChange={(e) => handleChange('accent_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.accent_color}
                        onChange={(e) => handleChange('accent_color', e.target.value)}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Header Background</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.header_bg_color}
                        onChange={(e) => handleChange('header_bg_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.header_bg_color}
                        onChange={(e) => handleChange('header_bg_color', e.target.value)}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="h-5 w-5" />
                  Typography
                </CardTitle>
                <CardDescription>
                  Choose the font family for the portal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>Font Family</Label>
                  <Select 
                    value={branding.font_family} 
                    onValueChange={(val) => handleChange('font_family', val)}
                  >
                    <SelectTrigger data-testid="font-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map(font => (
                        <SelectItem 
                          key={font.value} 
                          value={font.value}
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Welcome Message */}
            <Card>
              <CardHeader>
                <CardTitle>Welcome Message</CardTitle>
                <CardDescription>
                  Custom message shown on the portal login page
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={branding.welcome_message || ''}
                  onChange={(e) => handleChange('welcome_message', e.target.value)}
                  placeholder="Welcome to our compliance portal..."
                  rows={3}
                  data-testid="welcome-message"
                />
              </CardContent>
            </Card>
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Preview
                </CardTitle>
                <CardDescription>
                  Live preview of your branding
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Mini Preview */}
                <div 
                  className="rounded-lg overflow-hidden border shadow-sm"
                  style={{ fontFamily: branding.font_family }}
                >
                  {/* Header Preview */}
                  <div 
                    className="h-12 flex items-center px-3 gap-2"
                    style={{ backgroundColor: branding.header_bg_color }}
                  >
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo" 
                        className="h-6 object-contain"
                      />
                    ) : (
                      <div 
                        className="h-6 w-20 rounded bg-white/20"
                      />
                    )}
                  </div>
                  
                  {/* Sidebar + Content Preview */}
                  <div className="flex h-48">
                    {/* Sidebar */}
                    <div 
                      className="w-12 p-2 space-y-2"
                      style={{ backgroundColor: branding.sidebar_bg_color }}
                    >
                      <div 
                        className="h-2 rounded"
                        style={{ backgroundColor: branding.primary_color }}
                      />
                      <div className="h-2 rounded bg-white/10" />
                      <div className="h-2 rounded bg-white/10" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-3 bg-background">
                      <div 
                        className="h-3 w-20 rounded mb-2"
                        style={{ backgroundColor: branding.primary_color }}
                      />
                      <div className="space-y-1">
                        <div className="h-2 w-full rounded bg-muted" />
                        <div className="h-2 w-3/4 rounded bg-muted" />
                      </div>
                      <div className="flex gap-1 mt-3">
                        <div 
                          className="h-6 w-14 rounded text-[8px] flex items-center justify-center text-white"
                          style={{ backgroundColor: branding.primary_color }}
                        >
                          Button
                        </div>
                        <div 
                          className="h-6 w-14 rounded text-[8px] flex items-center justify-center border"
                          style={{ borderColor: branding.accent_color, color: branding.accent_color }}
                        >
                          Link
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color Swatches */}
                <div className="mt-4 grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded-lg mb-1"
                      style={{ backgroundColor: branding.primary_color }}
                    />
                    <span className="text-[10px] text-muted-foreground">Primary</span>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded-lg mb-1"
                      style={{ backgroundColor: branding.secondary_color }}
                    />
                    <span className="text-[10px] text-muted-foreground">Secondary</span>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded-lg mb-1"
                      style={{ backgroundColor: branding.accent_color }}
                    />
                    <span className="text-[10px] text-muted-foreground">Accent</span>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded-lg mb-1"
                      style={{ backgroundColor: branding.header_bg_color }}
                    />
                    <span className="text-[10px] text-muted-foreground">Header</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
